import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CSpinner } from '@coreui/react';
import {
  customerGet, customerPost, customerPut, customerDelete,
  logoutCustomer, isCustomerLoggedIn,
} from '../../services/customerAuth';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const STATUS_LABELS = {
  'Selesai': 'Completed', 'Waiting to Pickup': 'Ready for Pickup', 'Dipulangkan': 'Returned',
};

function PointsBadge({ points }) {
  return (
    <div style={{
      background: 'linear-gradient(135deg, #8bc34a, #6a9e2f)',
      borderRadius: 12, padding: '16px 24px', textAlign: 'center', color: '#fff',
      boxShadow: '0 4px 16px rgba(139,195,74,0.35)',
    }}>
      <div style={{ fontSize: 11, letterSpacing: 2, opacity: 0.85, marginBottom: 4 }}>AVAILABLE POINTS</div>
      <div style={{ fontSize: 48, fontWeight: 900, lineHeight: 1 }}>{points.toLocaleString()}</div>
      <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>FMZ Points</div>
    </div>
  );
}

// Strip /api so image URLs resolve to the Express server root, not React's PUBLIC_URL.
const _apiUrl = process.env.REACT_APP_API_URL || '';
const MEDIA_BASE = _apiUrl.endsWith('/api')
  ? _apiUrl.slice(0, -4)
  : (typeof window !== 'undefined' ? window.location.origin : '');

// Resize an image File to max 500px on the longest side before upload (client-side, no server dependency)
function resizeImageClient(file, maxPx = 500) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxPx || height > maxPx) {
        if (width >= height) { height = Math.round(height * maxPx / width); width = maxPx; }
        else { width = Math.round(width * maxPx / height); height = maxPx; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('toBlob failed')), 'image/jpeg', 0.82);
    };
    img.onerror = reject;
    img.src = url;
  });
}

// ── Rating Modal ──────────────────────────────────────────────────────────────
function RatingModal({ sessionId, staffName, onDone }) {
  const [rating, setRating]   = useState(0);
  const [hover, setHover]     = useState(0);
  const [saving, setSaving]   = useState(false);

  async function submit() {
    if (!rating) return;
    setSaving(true);
    try {
      await customerPost('/customer/chat/rate', { session_id: sessionId, rating });
      onDone(rating);
    } catch {}
    setSaving(false);
  }

  async function skip() {
    try { await customerPost('/customer/chat/rate', { session_id: sessionId, skip: true }); } catch {}
    onDone(null);
  }

  return (
    <div style={{
      position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 14,
    }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: '24px 20px', width: 260, textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#0d3a4a', marginBottom: 6 }}>Rate this conversation</div>
        {staffName && <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 14 }}>with {staffName}</div>}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 18 }}>
          {[1,2,3,4,5].map(s => (
            <span key={s} onMouseEnter={() => setHover(s)} onMouseLeave={() => setHover(0)} onClick={() => setRating(s)}
              style={{ fontSize: 28, cursor: 'pointer', color: s <= (hover || rating) ? '#f1c40f' : '#ddd', transition: 'color 0.1s' }}>★</span>
          ))}
        </div>
        <button onClick={submit} disabled={!rating || saving}
          style={{ width: '100%', padding: '9px 0', background: '#8bc34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', marginBottom: 8 }}>
          {saving ? '…' : 'Submit'}
        </button>
        <button onClick={skip}
          style={{ width: '100%', padding: '7px 0', background: 'none', color: '#aaa', border: 'none', fontSize: 13, cursor: 'pointer' }}>
          Skip, don't ask again
        </button>
      </div>
    </div>
  );
}

// ── Live Chat ─────────────────────────────────────────────────────────────────
function LiveChat({ customerUid }) {
  const [open, setOpen]               = useState(false);
  const [messages, setMessages]       = useState([]);
  const [input, setInput]             = useState('');
  const [sending, setSending]         = useState(false);
  const [lastId, setLastId]           = useState(0);
  const [staffOnline, setStaffOnline] = useState(false);
  const [imgFile, setImgFile]         = useState(null);
  const [imgPreview, setImgPreview]   = useState(null);
  const [session, setSession]         = useState(null); // { id, status, rating, rating_skipped }
  const [showRating, setShowRating]   = useState(false);
  const [ending, setEnding]           = useState(false);
  const bottomRef = useRef(null);
  const pollRef   = useRef(null);
  const fileRef   = useRef(null);

  const sessionEnded = session?.status === 'ended';
  const needsRating  = sessionEnded && session?.rating === null && !session?.rating_skipped;

  // Poll helpdesk status every 30s + real-time WS
  useEffect(() => {
    async function checkStatus() {
      try {
        const { data } = await customerGet('/customer/chat/status');
        setStaffOnline(!!data.online);
      } catch {}
    }
    checkStatus();
    const t = setInterval(checkStatus, 30000);

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    let wsConn;
    try {
      wsConn = new window.WebSocket(`${proto}://${window.location.host}/ws`);
      wsConn.onmessage = e => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'helpdesk:status') setStaffOnline(!!msg.online);
          if (msg.type === 'chat_session_ended' && msg.customer_uid === customerUid) {
            setSession(prev => prev ? { ...prev, status: 'ended' } : prev);
          }
        } catch {}
      };
    } catch {}

    return () => { clearInterval(t); if (wsConn) wsConn.close(); };
  }, [customerUid]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await customerGet('/customer/chat', { since: lastId });
      setSession(data.session || null);
      if (data.messages.length > 0) {
        setMessages(prev => {
          const ids = new Set(prev.map(m => m.id));
          const fresh = data.messages.filter(m => !ids.has(m.id));
          if (!fresh.length) return prev;
          setLastId(fresh[fresh.length - 1].id);
          return [...prev, ...fresh];
        });
      }
    } catch {}
  }, [lastId]);

  useEffect(() => {
    if (!open) return;
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, 4000);
    return () => clearInterval(pollRef.current);
  }, [open, fetchMessages]);

  // Show rating prompt automatically when session ends
  useEffect(() => {
    if (needsRating) setShowRating(true);
  }, [needsRating]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    try {
      const blob = await resizeImageClient(file);
      const resized = new File([blob], 'image.jpg', { type: 'image/jpeg' });
      setImgFile(resized);
      setImgPreview(URL.createObjectURL(blob));
    } catch {
      setImgFile(file);
      setImgPreview(URL.createObjectURL(file));
    }
  }

  function clearImage() {
    setImgFile(null);
    if (imgPreview) URL.revokeObjectURL(imgPreview);
    setImgPreview(null);
  }

  async function handleSend(e) {
    e.preventDefault();
    if (sending || sessionEnded) return;

    if (imgFile) {
      setSending(true);
      try {
        const form = new FormData();
        form.append('image', imgFile);
        const { data } = await customerPost('/customer/chat/image', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setMessages(prev => [...prev, data.message]);
        setLastId(data.message.id);
        clearImage();
      } catch {}
      setSending(false);
      return;
    }

    if (!input.trim()) return;
    setSending(true);
    try {
      const { data } = await customerPost('/customer/chat', { message: input.trim() });
      setMessages(prev => [...prev, data.message]);
      setLastId(data.message.id);
      setInput('');
    } catch {}
    setSending(false);
  }

  async function handleEndSession() {
    if (!window.confirm('End this chat session?')) return;
    setEnding(true);
    try {
      await customerPost('/customer/chat/end', {});
      setSession(prev => prev ? { ...prev, status: 'ended' } : prev);
      setShowRating(true);
    } catch {}
    setEnding(false);
  }

  async function handleNewChat() {
    try {
      await customerPost('/customer/chat/new', {});
      setMessages([]);
      setLastId(0);
      setSession(null);
      setShowRating(false);
    } catch {}
  }

  function handleRatingDone(r) {
    setShowRating(false);
    setSession(prev => prev ? { ...prev, rating: r, rating_skipped: r === null ? 1 : 0 } : prev);
  }

  // When opening chat and session was previously ended + unrated, show rating
  function handleOpen() {
    setOpen(o => {
      if (!o && needsRating) setShowRating(true);
      return !o;
    });
  }

  const staffName = messages.find(m => m.sender === 'staff')?.sender_name || null;

  return (
    <>
      {/* Floating button */}
      <button onClick={handleOpen} style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 500,
        width: 56, height: 56, borderRadius: '50%',
        background: '#8bc34a', border: 'none', cursor: 'pointer',
        boxShadow: '0 4px 16px rgba(139,195,74,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {open
          ? <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        }
        <span style={{
          position: 'absolute', bottom: 4, right: 4, width: 11, height: 11, borderRadius: '50%',
          background: staffOnline ? '#2ecc71' : '#95a5a6', border: '2px solid #fff',
        }} />
      </button>

      {/* Chat window */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 88, right: 24, zIndex: 500,
          width: 340, maxWidth: 'calc(100vw - 48px)',
          background: '#fff', borderRadius: 14,
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          {/* Rating overlay */}
          {showRating && (
            <RatingModal sessionId={session?.id} staffName={staffName} onDone={handleRatingDone} />
          )}

          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg, #071e2b, #0d3a4a)', padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                  background: staffOnline ? '#2ecc71' : '#95a5a6',
                  boxShadow: staffOnline ? '0 0 6px rgba(46,204,113,0.7)' : 'none',
                }} />
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Live Chat with Support</div>
              </div>
              {!sessionEnded && messages.length > 0 && (
                <button onClick={handleEndSession} disabled={ending}
                  style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}>
                  {ending ? '…' : 'End'}
                </button>
              )}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 3 }}>
              {sessionEnded
                ? 'Session ended'
                : staffOnline ? 'Support team is online' : "We'll get back when helpdesk staff is online"}
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 14, maxHeight: 280, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', color: '#aaa', fontSize: 13, marginTop: 20 }}>No messages yet. Say hello!</div>
            )}
            {messages.map(m => (
              <div key={m.id} style={{ alignSelf: m.sender === 'customer' ? 'flex-end' : 'flex-start', maxWidth: '80%' }}>
                {m.msg_type === 'image' && m.image_url ? (
                  <a href={`${MEDIA_BASE}${m.image_url}`} target="_blank" rel="noreferrer">
                    <img src={`${MEDIA_BASE}${m.image_url}`} alt="attachment" style={{ maxWidth: 200, borderRadius: 8, display: 'block' }} />
                  </a>
                ) : (
                  <div style={{
                    background: m.sender === 'customer' ? '#8bc34a' : '#f0f0f0',
                    color: m.sender === 'customer' ? '#fff' : '#222',
                    borderRadius: m.sender === 'customer' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                    padding: '8px 12px', fontSize: 13, lineHeight: 1.4,
                  }}>{m.message}</div>
                )}
                <div style={{ fontSize: 10, color: '#aaa', marginTop: 2, textAlign: m.sender === 'customer' ? 'right' : 'left' }}>
                  {m.sender_name} · {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Ended state */}
          {sessionEnded && (
            <div style={{ padding: '12px 14px', borderTop: '1px solid #f0f0f0', textAlign: 'center' }}>
              {!showRating && needsRating && (
                <button onClick={() => setShowRating(true)}
                  style={{ fontSize: 12, color: '#8bc34a', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 6, display: 'block', width: '100%' }}>
                  ★ Rate this conversation
                </button>
              )}
              <button onClick={handleNewChat}
                style={{ width: '100%', padding: '8px 0', background: '#8bc34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                Start New Conversation
              </button>
            </div>
          )}

          {/* Image preview strip */}
          {!sessionEnded && imgPreview && (
            <div style={{ padding: '8px 14px', borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src={imgPreview} alt="preview" style={{ height: 54, borderRadius: 6, objectFit: 'cover' }} />
              <button onClick={clearImage} style={{ background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: 20 }}>×</button>
            </div>
          )}

          {/* Input */}
          {!sessionEnded && (
          <form onSubmit={handleSend} style={{ display: 'flex', gap: 8, padding: '10px 14px', borderTop: '1px solid #f0f0f0', alignItems: 'center' }}>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
            <button type="button" onClick={() => fileRef.current?.click()}
              style={{ background: 'none', border: '1px solid #dde1e7', borderRadius: 8, padding: '7px 9px', cursor: 'pointer', color: '#6c757d', display: 'flex', alignItems: 'center' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            </button>
            <input
              value={input} onChange={e => setInput(e.target.value)}
              placeholder={imgFile ? 'Click → to send image' : 'Type a message…'}
              disabled={!!imgFile}
              style={{ flex: 1, padding: '8px 12px', border: '1px solid #dde1e7', borderRadius: 8, fontSize: 13, outline: 'none' }}
            />
            <button type="submit" disabled={sending || (!input.trim() && !imgFile)}
              style={{ padding: '8px 14px', background: '#8bc34a', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>
              {sending ? '…' : '→'}
            </button>
          </form>
          )}
        </div>
      )}
    </>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────
export default function CustomerDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [redeemLoading, setRedeemLoading] = useState('');
  const [redeemMsg, setRedeemMsg] = useState('');
  const [tab, setTab] = useState('jobs'); // 'jobs' | 'history' | 'profile'
  const [profileEmail, setProfileEmail] = useState('');
  const [profilePwd, setProfilePwd] = useState('');
  const [profileMsg, setProfileMsg] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await customerGet('/customer/dashboard');
      setData(d);
      setProfileEmail(d.customer.email || '');
    } catch (err) {
      if (err.response?.status === 401) { logoutCustomer(); navigate('/customer/login', { replace: true }); }
      else setError('Failed to load dashboard.');
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (!isCustomerLoggedIn()) { navigate('/customer/login', { replace: true }); return; }
    loadDashboard();
  }, [navigate, loadDashboard]);

  const googleBtnRef = useRef(null);

  useEffect(() => {
    if (tab !== 'profile' || !GOOGLE_CLIENT_ID || !data?.customer) return;
    if (data.customer.google_email) return;

    function renderBtn() {
      if (!window.google || !googleBtnRef.current) return;
      googleBtnRef.current.innerHTML = '';
      const w = googleBtnRef.current.getBoundingClientRect().width || 280;
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: Math.floor(w),
      });
    }

    function initGoogle() {
      if (!window.google || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (resp) => {
          setProfileMsg('');
          try {
            const { data: r } = await customerPost('/customer/google/link', { credential: resp.credential });
            setProfileEmail(r.email || '');
            setProfileMsg('Google connected successfully.');
            loadDashboard();
          } catch (err) {
            setProfileMsg(err.response?.data?.message || 'Failed to connect Google.');
          }
        },
        auto_select: false,
      });
      requestAnimationFrame(renderBtn);
    }

    let ro;
    if (typeof ResizeObserver !== 'undefined' && googleBtnRef.current) {
      ro = new ResizeObserver(() => { if (window.google) renderBtn(); });
      ro.observe(googleBtnRef.current);
    }

    if (typeof window.google !== 'undefined') {
      initGoogle();
      return () => { ro && ro.disconnect(); };
    }

    const existing = document.getElementById('google-gsi-script');
    if (existing) {
      existing.addEventListener('load', initGoogle);
      return () => {
        existing.removeEventListener('load', initGoogle);
        ro && ro.disconnect();
      };
    }

    const script = document.createElement('script');
    script.id = 'google-gsi-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initGoogle;
    document.head.appendChild(script);
    return () => { ro && ro.disconnect(); };
  }, [tab, data?.customer?.google_email, loadDashboard]);

  async function handleRedeem(jobcode) {
    setRedeemLoading(jobcode);
    setRedeemMsg('');
    try {
      const { data: d } = await customerPost('/customer/redeem', { jobcode });
      setRedeemMsg(d.message);
      loadDashboard();
    } catch (err) {
      setRedeemMsg(err.response?.data?.message || 'Redemption failed.');
    } finally { setRedeemLoading(''); }
  }

  async function handleSaveProfile(e) {
    e.preventDefault();
    setSavingProfile(true);
    setProfileMsg('');
    try {
      await customerPut('/customer/profile', { email: profileEmail, password: profilePwd || undefined });
      setProfileMsg('Profile updated successfully.');
      setProfilePwd('');
      loadDashboard();
    } catch (err) {
      setProfileMsg(err.response?.data?.message || 'Update failed.');
    } finally { setSavingProfile(false); }
  }

  async function handleGoogleUnlink() {
    if (!window.confirm('Disconnect Google? You can still sign in with your phone and password.')) return;
    setProfileMsg('');
    try {
      await customerDelete('/customer/google/link');
      setProfileMsg('Google disconnected successfully.');
      loadDashboard();
    } catch (err) {
      setProfileMsg(err.response?.data?.message || 'Failed to disconnect Google.');
    }
  }

  function handleLogout() {
    logoutCustomer();
    navigate('/customer/login', { replace: true });
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f4f6f9' }}>
        <CSpinner style={{ color: '#8bc34a' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f4f6f9' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#721c24', marginBottom: 12 }}>{error}</div>
          <button onClick={loadDashboard} style={{ padding: '8px 20px', background: '#8bc34a', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer' }}>Retry</button>
        </div>
      </div>
    );
  }

  const { customer, points, jobs, transactions, loyaltyConfig } = data;

  // Which jobs already have points redeemed
  const redeemedJobs = new Set(transactions.filter(t => t.type === 'job_earn').map(t => t.ref_jobcode));

  const inp = { width: '100%', padding: '10px 12px', border: '1px solid #dde1e7', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none', marginBottom: 12 };

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", minHeight: '100vh', background: '#f4f6f9' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #071e2b, #0d3a4a)', padding: '0' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={`${process.env.PUBLIC_URL}/images/fmzlogo.png`} alt="FMZ" style={{ height: 40, objectFit: 'contain' }} />
            <div>
              <div style={{ color: '#8bc34a', fontWeight: 700, fontSize: 15 }}>FMZ Points</div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>{customer.nama}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Link to="/track" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, textDecoration: 'none' }}>Track Repair</Link>
            <button onClick={handleLogout} style={{ padding: '6px 14px', background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
              Logout
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px' }}>
        {/* Points card */}
        <div style={{ marginBottom: 24 }}>
          <PointsBadge points={points} />
          {loyaltyConfig && (
            <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, background: '#fff', borderRadius: 8, padding: '12px 16px', borderLeft: '3px solid #8bc34a', minWidth: 180 }}>
                <div style={{ fontSize: 11, color: '#8c9bab', marginBottom: 4 }}>REDEMPTION RATE</div>
                <div style={{ fontWeight: 700, color: '#0d3a4a' }}>{loyaltyConfig.pointsPerRm} pts = RM1.00 discount</div>
              </div>
              <div style={{ flex: 1, background: '#fff', borderRadius: 8, padding: '12px 16px', borderLeft: '3px solid #8bc34a', minWidth: 180 }}>
                <div style={{ fontSize: 11, color: '#8c9bab', marginBottom: 4 }}>EARN ON REPAIRS</div>
                <div style={{ fontWeight: 700, color: '#0d3a4a' }}>RM{loyaltyConfig.minLow}–{loyaltyConfig.minHigh}: {loyaltyConfig.ptsLow} pts · Above RM{loyaltyConfig.minHigh}: {loyaltyConfig.ptsHigh} pts</div>
              </div>
            </div>
          )}
        </div>

        {redeemMsg && (
          <div style={{
            background: redeemMsg.includes('failed') || redeemMsg.includes('already') ? '#fff3f3' : '#f0f9e8',
            border: `1px solid ${redeemMsg.includes('failed') || redeemMsg.includes('already') ? '#f5c6cb' : '#c5e39a'}`,
            borderRadius: 8, padding: '12px 16px',
            color: redeemMsg.includes('failed') || redeemMsg.includes('already') ? '#721c24' : '#3a6b1a',
            fontSize: 14, marginBottom: 16,
          }}>
            {redeemMsg}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '2px solid #e8ecf0', marginBottom: 20 }}>
          {[['jobs', 'Completed Jobs'], ['history', 'Points History'], ['profile', 'My Profile']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{ padding: '10px 20px', background: 'none', border: 'none', fontWeight: tab === key ? 700 : 400, color: tab === key ? '#8bc34a' : '#6c757d', borderBottom: tab === key ? '2px solid #8bc34a' : '2px solid transparent', marginBottom: -2, cursor: 'pointer', fontSize: 14 }}>
              {label}
            </button>
          ))}
        </div>

        {/* Jobs tab */}
        {tab === 'jobs' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {jobs.length === 0 && (
              <div style={{ textAlign: 'center', padding: '32px 0', color: '#6c757d' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🔧</div>
                No completed jobs found.
              </div>
            )}
            {jobs.map(job => {
              const kos = parseFloat(job.kos || '0');
              const redeemed = redeemedJobs.has(job.jobcode);
              let earnable = 0;
              if (loyaltyConfig) {
                if (kos >= loyaltyConfig.minHigh) earnable = loyaltyConfig.ptsHigh;
                else if (kos >= loyaltyConfig.minLow) earnable = loyaltyConfig.ptsLow;
              }

              return (
                <div key={job.jobcode} style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, color: '#0d3a4a', fontSize: 15 }}>#{job.jobcode}</div>
                    <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>{job.jenis || 'Repair'} · {job.date}</div>
                    <div style={{ fontSize: 13, color: '#0d3a4a', marginTop: 4 }}>
                      Cost: <strong>RM{kos.toFixed(2)}</strong>
                      {' · '}
                      <span style={{ color: STATUS_LABELS[job.status] ? '#5a8a2e' : '#888' }}>{STATUS_LABELS[job.status] || job.status}</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {redeemed ? (
                      <div style={{ background: '#e8f5e9', color: '#388e3c', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 700 }}>
                        ✓ Points Earned
                      </div>
                    ) : earnable > 0 ? (
                      <button
                        onClick={() => handleRedeem(job.jobcode)}
                        disabled={!!redeemLoading}
                        style={{ padding: '8px 16px', background: '#8bc34a', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                      >
                        {redeemLoading === job.jobcode ? <CSpinner size="sm" style={{ color: '#fff' }} /> : `Redeem +${earnable} pts`}
                      </button>
                    ) : (
                      <div style={{ color: '#aaa', fontSize: 12 }}>Below minimum (RM{loyaltyConfig?.minLow})</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* History tab */}
        {tab === 'history' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {transactions.length === 0 && (
              <div style={{ textAlign: 'center', padding: '32px 0', color: '#6c757d' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                No transactions yet.
              </div>
            )}
            {transactions.map((t, i) => (
              <div key={i} style={{ background: '#fff', borderRadius: 10, padding: '14px 18px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 13, color: '#0d3a4a', fontWeight: 600 }}>{t.note}</div>
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 3 }}>{new Date(t.created_at).toLocaleDateString()}</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: 16, color: t.points > 0 ? '#5a8a2e' : '#c0392b' }}>
                  {t.points > 0 ? '+' : ''}{t.points}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Profile tab */}
        {tab === 'profile' && (
          <div style={{ background: '#fff', borderRadius: 10, padding: '24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', maxWidth: 420 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#0d3a4a', marginBottom: 6 }}>{customer.nama}</div>
            <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 16 }}>Phone: {customer.telhp}</div>

            {GOOGLE_CLIENT_ID && (
              <div style={{
                marginBottom: 20, padding: '14px 16px',
                background: '#f8f9fb', borderRadius: 8, border: '1px solid #e8ecf0',
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 8 }}>Google</div>
                {customer.google_email ? (
                  <div>
                    <div style={{ fontSize: 13, color: '#0d3a4a', marginBottom: 10 }}>
                      Connected: <strong>{customer.google_email}</strong>
                    </div>
                    <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 10 }}>
                      You can sign in with Google on the login page. Your profile email matches this Google account.
                    </div>
                    <button
                      type="button"
                      onClick={handleGoogleUnlink}
                      style={{
                        padding: '8px 14px', background: '#fff', color: '#721c24',
                        border: '1px solid #f5c6cb', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600,
                      }}
                    >
                      Disconnect Google
                    </button>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 10, lineHeight: 1.45 }}>
                      Connect your Google account to sign in faster and sync your email to your Google address.
                    </div>
                    <div ref={googleBtnRef} style={{ display: 'flex', justifyContent: 'flex-start', minHeight: 44 }} />
                  </div>
                )}
              </div>
            )}

            {profileMsg && (
              <div style={{
                background: profileMsg.includes('success') ? '#f0f9e8' : '#fff3f3',
                border: `1px solid ${profileMsg.includes('success') ? '#c5e39a' : '#f5c6cb'}`,
                borderRadius: 6, padding: '10px 14px',
                color: profileMsg.includes('success') ? '#3a6b1a' : '#721c24',
                fontSize: 13, marginBottom: 16,
              }}>
                {profileMsg}
              </div>
            )}
            <form onSubmit={handleSaveProfile}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Email</label>
              <input style={inp} type="email" value={profileEmail} onChange={e => setProfileEmail(e.target.value)} placeholder="your@email.com" />
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>New Password (leave blank to keep current)</label>
              <input style={inp} type="password" value={profilePwd} onChange={e => setProfilePwd(e.target.value)} placeholder="Min. 6 characters" minLength={6} />
              <button type="submit" disabled={savingProfile}
                style={{ padding: '10px 24px', background: '#8bc34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                {savingProfile ? <CSpinner size="sm" style={{ color: '#fff' }} /> : 'Save Changes'}
              </button>
            </form>
          </div>
        )}
      </div>

      <LiveChat customerUid={customer.uid} />
    </div>
  );
}
